import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DestinationsCarousel.css';
import { images } from '../../shared/assets';
import { initializeDragScroll } from '../../../utils/interactiveFeatures';

// Destination data from Figma design node 503:6032
const destinations = [
  {
    id: 'atacama',
    name: 'Atacama',
    location: 'San Pedro de atacama',
    country: 'northern chile',
    description: '12-Suite lodge in the ancient town of San Pedro de Atacama. Private Guide & 4WD per room and Relais & Châteaux restaurant for guests only.',
    color: '#7b6569',
    textColor: '#f3f3f3',
    graphic: images.destinationsPage.figmaAssets.destinationCarousel.atacamaGraphic,
    image: images.destinationsPage.figmaAssets.destinationCarousel.atacamaImage,
    buttonStyle: 'light'
  },
  {
    id: 'patagonia',
    name: 'Patagonia',
    location: 'Torres del Paine',
    country: 'northern chile',
    description: 'Luxury villas in the heart of Patagonia with stunning views of Torres del Paine. Private Guide & 4WD per room and Relais & Châteaux restaurant for guests only.',
    color: '#c0c6c8',
    textColor: '#49413d',
    graphic: images.destinationsPage.figmaAssets.destinationCarousel.patagoniaGraphic,
    image: images.destinationsPage.figmaAssets.destinationCarousel.patagoniaImage,
    buttonStyle: 'dark'
  },
  {
    id: 'iguazu',
    name: 'Iguazu',
    location: 'Puerto Iguazu',
    country: 'Northern Argentina',
    description: '14 private Villas surrounded by Atlantic Rainforest. Private Guide & 4WD per room and Relais & Châteaux restaurant for guests only.',
    color: '#3e5b58',
    textColor: '#f3f3f3',
    graphic: images.destinationsPage.figmaAssets.destinationCarousel.iguazuGraphic,
    image: images.destinationsPage.figmaAssets.destinationCarousel.iguazuVideo,
    buttonStyle: 'light'
  },
  {
    id: 'santa-catarina',
    name: 'Santa Catarina',
    location: 'Santa Catarina',
    country: 'brazil',
    description: 'Dui diam sit duis amet sit tellus turpis sit ut. Orci molestie nisi lectus proin nullam et eu. Feugiat amet in nam arcu mattis id laoreet. Feugiat enim.',
    color: '#009485',
    textColor: '#f3f3f3',
    graphic: images.destinationsPage.figmaAssets.destinationCarousel.santaCatarinaGraphic,
    image: images.destinationsPage.figmaAssets.destinationCarousel.santaCatarinaImage,
    buttonStyle: 'light'
  },
  {
    id: 'mendoza',
    name: 'Mendoza',
    location: 'cavas',
    country: 'argentina',
    description: 'Est nibh tristique ipsum interdum. Faucibus nulla fringilla et vel nulla non maecenas lectus dapibus. Morbi cras varius nisl congue quisque ornare in.',
    color: '#502630',
    textColor: '#f3f3f3',
    graphic: images.destinationsPage.figmaAssets.destinationCarousel.mendozaGraphic,
    image: images.destinationsPage.figmaAssets.destinationCarousel.mendozaVideo,
    buttonStyle: 'light'
  }
];

interface DestinationPanelProps {
  destination: typeof destinations[0];
  onVisitProperty: (destinationId: string) => void;
}

const DestinationPanel: React.FC<DestinationPanelProps> = ({ destination, onVisitProperty }) => {
  return (
    <div className="destinations-carousel__panel">
      {/* Info Panel - 513px width */}
      <div 
        className="destinations-carousel__info-panel" 
        style={{ backgroundColor: destination.color }}
      >
        {/* Background Graphic */}
        <div 
          className="destinations-carousel__graphic"
          style={{ backgroundImage: `url('${destination.graphic}')` }}
        />
        
        {/* Content */}
        <div className="destinations-carousel__content">
          {/* Header */}
          <div className="destinations-carousel__header">
            <h2 
              className="destinations-carousel__title"
              style={{ color: destination.textColor }}
            >
              {destination.name}
            </h2>
            <div 
              className="destinations-carousel__location" 
              style={{ color: destination.textColor }}
            >
              <span>{destination.location}</span>
              <span>-</span>
              <span>{destination.country}</span>
            </div>
          </div>
          
          {/* Body + Button */}
          <div className="destinations-carousel__body">
            <p 
              className="destinations-carousel__description"
              style={{ color: destination.textColor }}
            >
              {destination.description}
            </p>
            <button 
              className={`destinations-carousel__button ${destination.buttonStyle}`}
              onClick={() => onVisitProperty(destination.id)}
            >
              <div 
                className="destinations-carousel__button-border"
                style={{ borderColor: destination.textColor }}
              />
              <div 
                className="destinations-carousel__button-text"
                style={{ color: destination.textColor }}
              >
                <p>visit the property</p>
              </div>
            </button>
          </div>
        </div>
      </div>
      
      {/* Image/Video - Dynamic rendering based on content type */}
      {destination.id === 'iguazu' || destination.id === 'mendoza' ? (
        <div className="destinations-carousel__video-container">
          <video 
            className="destinations-carousel__video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={destination.image} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      ) : (
        <div 
          className="destinations-carousel__image"
          style={{ backgroundImage: `url('${destination.image}')` }}
        />
      )}
    </div>
  );
};

export const DestinationsCarousel: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleVisitProperty = (destinationId: string) => {
    // Navigate to the appropriate property page based on destination ID
    switch (destinationId.toLowerCase()) {
      case 'atacama':
        navigate('/atacama');
        break;
      case 'patagonia':
        navigate('/patagonia');
        break;
      case 'iguazu':
        navigate('/iguazu');
        break;
      case 'santa-catarina':
        navigate('/santa-catarina');
        break;
      case 'mendoza':
        navigate('/mendoza');
        break;
      default:
        navigate('/packages');
        break;
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      // Initialize drag scroll functionality
      const cleanup = initializeDragScroll('draggable-carousel');
      
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, []);

  return (
    <section className="destinations-carousel" data-scroll-section>
      <div 
        ref={scrollRef}
        className="horizontal-scroll-carousel__scroll-container"
        id="draggable-carousel"
      >
        {destinations.map((destination) => (
          <DestinationPanel
            key={destination.id}
            destination={destination}
            onVisitProperty={handleVisitProperty}
          />
        ))}
      </div>
    </section>
  );
};